import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { OriginalsNav } from '@/components/OriginalsNav';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ShoppingBag, ArrowLeft, Plus, Minus, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OriginalsProduct() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const { addToCart, cart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      // Initialize selected options with first value of each option
      if (response.data.options) {
        const initialOptions = {};
        Object.keys(response.data.options).forEach(key => {
          if (response.data.options[key]?.length > 0) {
            initialOptions[key] = response.data.options[key][0];
          }
        });
        setSelectedOptions(initialOptions);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Check if all required options are selected
    if (product.options) {
      const missingOptions = Object.keys(product.options).filter(
        key => !selectedOptions[key]
      );
      if (missingOptions.length > 0) {
        toast.error(`Please select: ${missingOptions.join(', ')}`);
        return;
      }
    }
    addToCart(product, quantity, selectedOptions);
    toast.success(`${product.name} added to cart`);
  };

  const isInCart = cart.some(item => item.product.id === id);

  const formatCategory = (cat) => {
    return cat?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get gallery images or fallback to main image
  const galleryImages = product?.gallery_images?.length > 0 
    ? product.gallery_images 
    : product?.image_url ? [product.image_url] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <OriginalsNav />
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse grid lg:grid-cols-2 gap-16">
              <div className="bg-[#171717] aspect-square rounded-lg" />
              <div className="space-y-4">
                <div className="bg-[#171717] h-4 w-1/4 rounded" />
                <div className="bg-[#171717] h-10 w-3/4 rounded" />
                <div className="bg-[#171717] h-24 rounded" />
                <div className="bg-[#171717] h-8 w-1/4 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <OriginalsNav />
        <div className="pt-32 pb-20 px-6 text-center">
          <h1 className="font-playfair text-3xl text-white mb-4">Product Not Found</h1>
          <Link to="/originals" className="text-[#d4af37] hover:underline">
            Return to Gallery
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="originals-product">
      <OriginalsNav />
      
      <main className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          {/* Back Link */}
          <Link 
            to="/originals"
            className="inline-flex items-center gap-2 text-[#a1a1aa] hover:text-[#d4af37] font-cormorant mb-8 transition-colors"
            data-testid="back-to-gallery"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Gallery
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative rounded-lg overflow-hidden bg-[#171717]">
                <img 
                  src={galleryImages[selectedImage]} 
                  alt={product.name}
                  className="w-full h-auto object-contain max-h-[600px] mx-auto"
                />
                {/* Spotlight effect */}
                <div className="absolute inset-0 pointer-events-none spotlight rounded-lg" />
              </div>

              {/* Thumbnail Gallery */}
              {galleryImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {galleryImages.map((img, index) => (
                    <button
                      key={`thumb-${img}-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImage === index 
                          ? 'border-[#d4af37]' 
                          : 'border-transparent hover:border-[#a1a1aa]'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img 
                        src={img} 
                        alt={`${product.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="flex flex-col">
              <span className="font-cinzel text-xs text-[#d4af37] tracking-widest uppercase mb-4">
                {formatCategory(product.category)}
              </span>
              
              <h1 className="font-playfair text-3xl lg:text-4xl text-white mb-6">
                {product.name}
              </h1>
              
              <p className="font-cormorant text-lg text-[#a1a1aa] mb-8 leading-relaxed">
                {product.description}
              </p>

              <div className="border-t border-[#27272a] pt-8 mb-8">
                <p className="font-mono text-3xl text-[#d4af37] mb-6">
                  £{Math.floor(product.price)}
                </p>

                {/* Product Options */}
                {product.options && Object.keys(product.options).length > 0 && (
                  <div className="space-y-4 mb-6">
                    {Object.entries(product.options).map(([optionName, optionValues]) => (
                      <div key={optionName} className="space-y-2">
                        <label className="font-cormorant text-[#a1a1aa] capitalize">
                          {optionName}
                        </label>
                        <Select 
                          value={selectedOptions[optionName] || ''} 
                          onValueChange={(value) => setSelectedOptions({...selectedOptions, [optionName]: value})}
                        >
                          <SelectTrigger 
                            className="w-full bg-[#171717] border-[#27272a] text-white font-cormorant"
                            data-testid={`option-${optionName}`}
                          >
                            <SelectValue placeholder={`Select ${optionName}`} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#171717] border-[#27272a]">
                            {optionValues.map((value) => (
                              <SelectItem 
                                key={value} 
                                value={value}
                                className="text-white font-cormorant hover:bg-[#27272a]"
                              >
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quantity Selector */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-cormorant text-[#a1a1aa]">Quantity</span>
                  <div className="flex items-center border border-[#27272a] rounded-full">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:text-[#d4af37] transition-colors"
                      data-testid="decrease-quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="font-mono w-12 text-center">{quantity}</span>
                    <button 
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:text-[#d4af37] transition-colors"
                      data-testid="increase-quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Add to Cart Button */}
                <Button
                  onClick={handleAddToCart}
                  className={`w-full py-6 rounded-full font-cinzel tracking-wider text-sm ${
                    isInCart 
                      ? 'bg-[#171717] border border-[#d4af37] text-[#d4af37] hover:bg-[#27272a]'
                      : 'bg-[#d4af37] text-black hover:bg-[#cd7f32]'
                  }`}
                  data-testid="add-to-cart-btn"
                >
                  {isInCart ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      In Cart — Add More
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Add to Cart
                    </>
                  )}
                </Button>

                {/* View Cart Link */}
                {isInCart && (
                  <Link 
                    to="/originals/cart"
                    className="block text-center mt-4 text-[#d4af37] font-cormorant hover:underline"
                    data-testid="view-cart-link"
                  >
                    View Cart →
                  </Link>
                )}
              </div>

              {/* Product Details */}
              <div className="mt-auto border-t border-[#27272a] pt-8">
                <h3 className="font-cinzel text-xs tracking-widest text-[#a1a1aa] mb-4">DETAILS</h3>
                <ul className="space-y-2 font-cormorant text-[#a1a1aa]">
                  {product.details ? (
                    product.details.split('\n').filter(d => d.trim()).map((detail, i) => (
                      <li key={`detail-${detail.trim().slice(0, 20)}-${i}`}>• {detail.trim()}</li>
                    ))
                  ) : (
                    <>
                      <li>• Handcrafted unique piece</li>
                      <li>• Hand finished to a beautiful tactile texture</li>
                      <li>• Custom finishes available including clear lacquer, powdercoat, paint, rust</li>
                      <li>• Free UK delivery</li>
                      <li>• Worldwide shipping available</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272a] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="font-cormorant text-sm text-[#a1a1aa] text-center">
            © 2024 Ben Jefferies Originals. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
